import { BaseScene } from './BaseScene';
import { t } from '../i18n/lang';
import { UserProfile, saveUserProfile, getEquipmentManager } from '../entities/base/UserProfile';

export class GameOverScene extends BaseScene {
  private userProfile: UserProfile;

  constructor() {
    super({ key: 'GameOver' });
  }

  override create(): void {
    super.create();
    this.addBackground();
    
    // Lấy user profile
    this.userProfile = (window as any).userProfile;
    
    // Lấy dữ liệu từ GamePlayScene
    const gameData = this.scene.get('GamePlay').data;
    const finalLevel = gameData.get('finalLevel') || 1;
    const monstersKilled = gameData.get('monstersKilled') || 0;
    const bossesKilled = gameData.get('bossesKilled') || 0;
    
    // Apply equipment bonuses to rewards
    const equipmentStats = getEquipmentManager().calculateTotalStats();
    const rubyBonus = 1 + (equipmentStats.rubyBonus || 0);
    
    // Tính toán phần thưởng
    const goldReward = finalLevel * 10 + monstersKilled * 2 + bossesKilled * 50;
    const baseRubyReward = Math.floor(finalLevel / 5) + bossesKilled * 5;
    const rubyReward = Math.floor(baseRubyReward * rubyBonus);
    
    // Cộng phần thưởng vào profile
    this.userProfile.gold += goldReward;
    this.userProfile.ruby += rubyReward;
    (window as any).userProfile = this.userProfile;
    saveUserProfile(this.userProfile);

    this.createGameOverScreen(finalLevel, monstersKilled, bossesKilled, goldReward, rubyReward, rubyBonus);
  }

  private createGameOverScreen(level: number, kills: number, bosses: number, goldReward: number, rubyReward: number, rubyBonus: number): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;
    
    // Create dramatic overlay
    this.createDramaticOverlay();
    
    // Game Over title with effects
    this.createGameOverTitle(centerX, centerY);
    
    // Mission summary panel
    this.createMissionSummary(level, kills, bosses, centerX, centerY);
    
    // Rewards panel
    this.createRewardsPanel(goldReward, rubyReward, rubyBonus, centerX, centerY);
    
    // Action buttons
    this.createActionButtons(centerX, centerY);
  }

  private createDramaticOverlay(): void {
    // Dark overlay with gradient
    const overlay = this.add.graphics();
    overlay.fillGradientStyle(0x000000, 0x1a1a2e, 0x000000, 0x1a1a2e, 0.8);
    overlay.fillRect(0, 0, this.cameras.main.width, this.cameras.main.height);
    overlay.setDepth(500);
    
    // Animated particles for atmosphere
    this.createAtmosphericParticles();
  }

  private createAtmosphericParticles(): void {
    // Create floating debris particles
    for (let i = 0; i < 20; i++) {
      this.time.delayedCall(i * 100, () => {
        const x = Phaser.Math.Between(-50, this.cameras.main.width + 50);
        const y = Phaser.Math.Between(-50, this.cameras.main.height + 50);
        
        const particle = this.add.circle(x, y, Phaser.Math.Between(2, 6), 0x95a5a6, 0.6);
        particle.setDepth(600);
        
        // Floating animation
        this.tweens.add({
          targets: particle,
          x: x + Phaser.Math.Between(-100, 100),
          y: y + Phaser.Math.Between(-100, 100),
          alpha: 0.1,
          duration: Phaser.Math.Between(3000, 6000),
          ease: 'Sine.easeInOut',
          onComplete: () => particle.destroy()
        });
      });
    }
  }

  private createGameOverTitle(centerX: number, centerY: number): void {
    const titleContainer = this.add.container(centerX, centerY - 200);
    titleContainer.setDepth(1000);
    
    // Title glow effect
    const titleGlow = this.add.text(0, 0, 'MISSION COMPLETE', {
      fontSize: '56px',
      fontFamily: 'Arial Black',
      color: '#e74c3c',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    titleContainer.add(titleGlow);
    
    // Main title
    const titleText = this.add.text(0, 0, 'MISSION COMPLETE', {
      fontSize: '52px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#2c3e50',
      strokeThickness: 4
    }).setOrigin(0.5);
    titleContainer.add(titleText);
    
    // Subtitle
    const subtitle = this.add.text(0, 60, 'Earth\'s defense systems offline', {
      fontSize: '18px',
      fontFamily: 'Arial',
      color: '#bdc3c7',
      fontStyle: 'italic'
    }).setOrigin(0.5);
    titleContainer.add(subtitle);
    
    // Entrance animation
    titleContainer.setAlpha(0);
    titleContainer.setScale(0.5);
    
    this.tweens.add({
      targets: titleContainer,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 800,
      ease: 'Back.easeOut'
    });
    
    // Pulsing glow
    this.tweens.add({
      targets: titleGlow,
      alpha: 0.6,
      scaleX: 1.05,
      scaleY: 1.05,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  private createMissionSummary(level: number, kills: number, bosses: number, centerX: number, centerY: number): void {
    const summaryContainer = this.add.container(centerX - 150, centerY - 80);
    summaryContainer.setDepth(1001);
    
    // Panel background
    const panelBg = this.add.graphics();
    panelBg.fillGradientStyle(0x2c3e50, 0x34495e, 0x2c3e50, 0x34495e, 0.95);
    panelBg.fillRoundedRect(0, 0, 300, 120, 15);
    panelBg.lineStyle(2, 0x74b9ff, 0.8);
    panelBg.strokeRoundedRect(0, 0, 300, 120, 15);
    summaryContainer.add(panelBg);
    
    // Panel title
    const titleText = this.add.text(150, 20, 'MISSION REPORT', {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#74b9ff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    summaryContainer.add(titleText);
    
    // Stats with icons
    const stats = [
      { icon: '🏆', label: 'Level Reached', value: level, color: '#f39c12' },
      { icon: '💀', label: 'Enemies Defeated', value: kills, color: '#e74c3c' },
      { icon: '👑', label: 'Bosses Eliminated', value: bosses, color: '#9b59b6' }
    ];
    
    stats.forEach((stat, index) => {
      const y = 50 + (index * 20);
      
      // Icon
      const icon = this.add.text(20, y, stat.icon, {
        fontSize: '16px',
        fontFamily: 'Arial'
      }).setOrigin(0, 0.5);
      summaryContainer.add(icon);
      
      // Label
      const label = this.add.text(45, y, stat.label, {
        fontSize: '12px',
        fontFamily: 'Arial',
        color: '#bdc3c7'
      }).setOrigin(0, 0.5);
      summaryContainer.add(label);
      
      // Value
      const value = this.add.text(280, y, stat.value.toString(), {
        fontSize: '14px',
        fontFamily: 'Arial Black',
        color: stat.color,
        fontStyle: 'bold'
      }).setOrigin(1, 0.5);
      summaryContainer.add(value);
    });
    
    // Entrance animation
    summaryContainer.setAlpha(0);
    summaryContainer.setX(centerX - 300);
    
    this.time.delayedCall(400, () => {
      this.tweens.add({
        targets: summaryContainer,
        alpha: 1,
        x: centerX - 150,
        duration: 600,
        ease: 'Power2.easeOut'
      });
    });
  }

  private createRewardsPanel(goldReward: number, rubyReward: number, rubyBonus: number, centerX: number, centerY: number): void {
    const rewardsContainer = this.add.container(centerX - 150, centerY + 60);
    rewardsContainer.setDepth(1002);
    
    // Panel background
    const panelBg = this.add.graphics();
    panelBg.fillGradientStyle(0x27ae60, 0x2ecc71, 0x27ae60, 0x2ecc71, 0.9);
    panelBg.fillRoundedRect(0, 0, 300, 100, 15);
    panelBg.lineStyle(2, 0xffffff, 1);
    panelBg.strokeRoundedRect(0, 0, 300, 100, 15);
    rewardsContainer.add(panelBg);
    
    // Panel title
    const titleText = this.add.text(150, 20, 'MISSION REWARDS', {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    rewardsContainer.add(titleText);
    
    // Gold reward
    const goldIcon = this.add.text(30, 50, '💰', {
      fontSize: '24px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    rewardsContainer.add(goldIcon);
    
    const goldText = this.add.text(60, 45, 'GOLD EARNED', {
      fontSize: '10px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    });
    rewardsContainer.add(goldText);
    
    const goldValue = this.add.text(60, 60, `+${goldReward}`, {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#f1c40f',
      fontStyle: 'bold'
    });
    rewardsContainer.add(goldValue);
    
    // Ruby reward
    const rubyIcon = this.add.text(180, 50, '💎', {
      fontSize: '24px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    rewardsContainer.add(rubyIcon);
    
    const rubyText = this.add.text(210, 45, 'RUBY EARNED', {
      fontSize: '10px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    });
    rewardsContainer.add(rubyText);
    
    const rubyValue = this.add.text(210, 60, `+${rubyReward}`, {
      fontSize: '16px',
      fontFamily: 'Arial Black',
      color: '#e74c3c',
      fontStyle: 'bold'
    });
    rewardsContainer.add(rubyValue);
    
    // Bonus indicator
    if (rubyBonus > 1) {
      const bonusText = this.add.text(150, 80, `Equipment Bonus: ${((rubyBonus - 1) * 100).toFixed(0)}%`, {
        fontSize: '10px',
        fontFamily: 'Arial',
        color: '#ffffff',
        fontStyle: 'italic'
      }).setOrigin(0.5);
      rewardsContainer.add(bonusText);
    }
    
    // Entrance animation
    rewardsContainer.setAlpha(0);
    rewardsContainer.setX(centerX + 150);
    
    this.time.delayedCall(800, () => {
      this.tweens.add({
        targets: rewardsContainer,
        alpha: 1,
        x: centerX - 150,
        duration: 600,
        ease: 'Power2.easeOut'
      });
    });
    
    // Reward collection animation
    this.time.delayedCall(1400, () => {
      // Gold collection effect
      this.tweens.add({
        targets: goldIcon,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 300,
        yoyo: true,
        ease: 'Power2'
      });
      
      // Ruby collection effect
      this.time.delayedCall(200, () => {
        this.tweens.add({
          targets: rubyIcon,
          scaleX: 1.5,
          scaleY: 1.5,
          duration: 300,
          yoyo: true,
          ease: 'Power2'
        });
      });
    });
  }

  private createActionButtons(centerX: number, centerY: number): void {
    const buttonsContainer = this.add.container(centerX, centerY + 200);
    buttonsContainer.setDepth(1003);
    
    // Try Again button
    const tryAgainContainer = this.add.container(-120, 0);
    const tryAgainBg = this.add.graphics();
    tryAgainBg.fillGradientStyle(0x3498db, 0x2980b9, 0x3498db, 0x2980b9, 1);
    tryAgainBg.fillRoundedRect(-80, -25, 160, 50, 25);
    tryAgainBg.lineStyle(2, 0xffffff, 1);
    tryAgainBg.strokeRoundedRect(-80, -25, 160, 50, 25);
    tryAgainContainer.add(tryAgainBg);
    
    const tryAgainIcon = this.add.text(-25, 0, '🔄', {
      fontSize: '20px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    tryAgainContainer.add(tryAgainIcon);
    
    const tryAgainText = this.add.text(15, 0, 'TRY AGAIN', {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    tryAgainContainer.add(tryAgainText);
    
    // Main Menu button
    const menuContainer = this.add.container(120, 0);
    const menuBg = this.add.graphics();
    menuBg.fillGradientStyle(0x95a5a6, 0x7f8c8d, 0x95a5a6, 0x7f8c8d, 1);
    menuBg.fillRoundedRect(-80, -25, 160, 50, 25);
    menuBg.lineStyle(2, 0xbdc3c7, 1);
    menuBg.strokeRoundedRect(-80, -25, 160, 50, 25);
    menuContainer.add(menuBg);
    
    const menuIcon = this.add.text(-25, 0, '🏠', {
      fontSize: '20px',
      fontFamily: 'Arial'
    }).setOrigin(0.5);
    menuContainer.add(menuIcon);
    
    const menuText = this.add.text(15, 0, 'MAIN MENU', {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    menuContainer.add(menuText);
    
    buttonsContainer.add(tryAgainContainer);
    buttonsContainer.add(menuContainer);
    
    // Interactive setup
    this.setupButtonInteraction(tryAgainContainer, () => {
      this.scene.start('EarthSelection');
    });
    
    this.setupButtonInteraction(menuContainer, () => {
      this.scene.start('MainMenu');
    });
    
    // Entrance animation
    buttonsContainer.setAlpha(0);
    buttonsContainer.setY(centerY + 250);
    
    this.time.delayedCall(1600, () => {
      this.tweens.add({
        targets: buttonsContainer,
        alpha: 1,
        y: centerY + 200,
        duration: 600,
        ease: 'Back.easeOut'
      });
    });
  }

  private setupButtonInteraction(button: Phaser.GameObjects.Container, callback: () => void): void {
    button.setInteractive(
      new Phaser.Geom.Rectangle(-80, -25, 160, 50),
      Phaser.Geom.Rectangle.Contains
    );
    
    button.on('pointerover', () => {
      this.tweens.add({
        targets: button,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    button.on('pointerout', () => {
      this.tweens.add({
        targets: button,
        scaleX: 1,
        scaleY: 1,
        duration: 200,
        ease: 'Power2'
      });
    });
    
    button.on('pointerdown', () => {
      this.tweens.add({
        targets: button,
        scaleX: 0.95,
        scaleY: 0.95,
        duration: 100,
        yoyo: true,
        ease: 'Power2',
        onComplete: callback
      });
    });
  }
} 